import { AppError } from '@app/errors/AppError'

import { checkErrorRecursively } from '@app/utils/checkErrorRecursively'
import { removeStackInitiator } from '@app/utils/removeStackInitiator'

export class FetchError extends AppError {}

/**
 * A 2xx body that ended mid-document. Chunked responses carry no Content-Length, so a connection
 * dropped mid-body still resolves from `text()` and the parse failure is the first sign of it — a
 * transport failure, not a bad payload. A `FetchError`, so `isNetworkFailError` already covers it.
 */
export class TruncatedResponseError extends FetchError {}

export class ApiError extends AppError {
  name = 'ApiError'
  description?: string
  response!: any

  /** The parsed JSON:API error envelope, when the failed response carried one. */
  withResponse(response: ApiErrorResponse | undefined) {
    this.response = response
    return this
  }

  static create(status: number, description: string, response: any) {
    const err = new ApiError(`${status} - ${description}`)
    err.status = status
    err.description = description
    err.response = response
    if (err.stack) {
      err.stack = removeStackInitiator(err.stack)
    }
    return err
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError

export const asApiError = (e: unknown) => (isApiError(e) ? e : null)

/**
 * True when the error — or anything in its `cause` chain — is a 401. Unlike `isApiError`, this
 * matches every `AppError` subclass, so a 401 raised by the internal api or by the token exchange
 * counts too. Callers use it to treat an init-time 401 as auth loss rather than a fatal error.
 */
export const isUnauthorizedError = (e: unknown): boolean => checkErrorRecursively(e, err => err instanceof AppError && err.status === 401)

export class RequestError extends AppError {
  name = 'RequestError'
  description?: string
  body: string = ''

  static create(status: number, description: string, body: string) {
    let message = `${status}`
    if (description) message += ` - ${description}`
    if (body) message += `: ${body}`
    const err = new RequestError(message)
    err.status = status
    err.description = description
    err.body = body
    if (err.stack) {
      err.stack = removeStackInitiator(err.stack)
    }
    return err
  }
}

export const isRequestError = (e: any): e is RequestError => e instanceof RequestError

export const asRequesError = (e: any) => (isRequestError(e) ? e : null)

export const isNetworkError = (e: any): e is ApiError | RequestError => isApiError(e) || isRequestError(e)

export const asNetworkError = (e: any) => (isNetworkError(e) ? e : null)

/**
 * Names an api failure by the first machine code its body carries. The dispatcher only knows the
 * status, so without this a 400 reads as "400 - Bad Request" wherever the message is what shows —
 * a Sentry title, or `extractErrors` falling through to it.
 */
export const liftPackagesApiError = (e: unknown): unknown => {
  if (!(e instanceof ApiError)) return e
  const code = getResponseErrorCodes(e.response)[0]
  if (!code || e.description) return e
  return ApiError.create(e.status, code, e.response).withHandled(e.handled).withCause(e)
}

/** Machine error codes from a JSON:API error body (`{errors: [{code}]}`), tolerant of any shape. */
export const getResponseErrorCodes = (response: unknown): string[] => {
  if (typeof response !== 'object' || response === null) return []
  const errors = (response as { errors?: unknown }).errors
  if (!Array.isArray(errors)) return []
  return errors.flatMap(item => {
    const code = (item as { code?: unknown } | null)?.code
    return typeof code === 'string' ? [code] : []
  })
}

export const isNetworkFailError = (e: unknown) =>
  checkErrorRecursively(e, e => e instanceof FetchError || (e instanceof Error && errorMessages.some(r => r.test(e.message))))

/**
 * Does this JSON.parse failure mean "the input ended early" rather than "the input is wrong"? Two
 * signals, because engines disagree: JSC names the condition in the message, while V8 reports a
 * position — and a failure at the very end of the input means the input ran out. A miss only costs
 * the downgrade; the error still surfaces the way it does today.
 */
export const isPrematureEndError = (e: unknown, source: string) => {
  if (!(e instanceof Error)) return false
  if (/unterminated string|unexpected end of (json )?input|unexpected eof|end of data/i.test(e.message)) return true
  const position = /at position (\d+)/i.exec(e.message)?.[1]
  return position !== undefined && Number(position) >= source.length
}

const errorMessages = [
  /^network error/i,
  /^failed to fetch/i,
  /^networkerror when attempting to fetch resource/i,
  /^the internet connection appears to be offline/i,
  /^load failed/i,
  /^network request failed/i,
  // iOS spelling of the same thing: RN's fetch polyfill rejects `ontimeout` with
  // this, where Android surfaces `Network request failed` (whatwg-fetch).
  /^network request timed out/i,
  /^fetch failed/i,
  /^terminated/i,
  /^write EPIPE/i,
]

/** JSON:API error envelope. The v3 item `status` is a string (e.g. "429"), unlike the v2 numeric one. */
export type ApiErrorResponse = {
  errors: ApiErrorResponseItem[]
}

export type ApiErrorResponseItem = {
  id?: string
  status?: string
  code?: string
  title?: string | null
  detail?: string | null
  source?: { pointer?: string; parameter?: string }
  meta?: { [key: string]: unknown }
}

/** Narrow an already-parsed body to the error envelope, or `undefined` if it isn't one. */
export const asApiErrorResponse = (value: unknown): ApiErrorResponse | undefined =>
  value && typeof value === 'object' && Array.isArray((value as { errors?: unknown }).errors) ? (value as ApiErrorResponse) : undefined
